import { useEffect, useState } from 'react';
import financasApi from '@/lib/financasApi';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wallet } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart } from 'recharts';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '-';

function defaultRange() {
  const fim = new Date();
  const inicio = new Date(fim);
  inicio.setDate(fim.getDate() - 30);
  return { inicio: inicio.toISOString().slice(0, 10), fim: fim.toISOString().slice(0, 10) };
}

export default function FinancasFluxoCaixa() {
  const [range, setRange] = useState(defaultRange());
  const [data, setData] = useState({ items: [], saldo_periodo: 0 });

  useEffect(() => {
    const params = new URLSearchParams({ inicio: range.inicio, fim: range.fim });
    financasApi.get(`/financas/fluxo-caixa?${params.toString()}`).then(({ data }) => setData(data)).catch(() => {});
  }, [range]);

  const chart = data.items.map(i => ({ data: fmtDate(i.data), Entradas: i.entradas, Saídas: i.saidas, Acumulado: i.saldo_acumulado }));

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="financas-fluxo-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3"><Wallet className="w-6 h-6 md:w-7 md:h-7 text-emerald-400" /> Fluxo de Caixa</h1>
          <p className="text-slate-400 text-xs md:text-sm mt-1">Saldo dia a dia (apenas transações pagas)</p>
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-slate-400 text-xs">Início</Label>
            <Input type="date" value={range.inicio} onChange={(e) => setRange({ ...range, inicio: e.target.value })} className="bg-slate-900 border-white/10 text-white" />
          </div>
          <div className="flex-1">
            <Label className="text-slate-400 text-xs">Fim</Label>
            <Input type="date" value={range.fim} onChange={(e) => setRange({ ...range, fim: e.target.value })} className="bg-slate-900 border-white/10 text-white" />
          </div>
        </div>
      </div>

      <div className="bg-slate-900 border border-white/10 rounded-xl p-5 mb-6">
        <p className="text-xs uppercase tracking-wider text-slate-400">Saldo do Período</p>
        <p className={`text-3xl font-bold mt-1 ${data.saldo_periodo >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmtBRL(data.saldo_periodo)}</p>
      </div>

      <div className="bg-slate-900 border border-white/10 rounded-xl p-5 mb-6">
        <h3 className="text-white font-semibold mb-4">Saldo Acumulado</h3>
        {chart.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-slate-500">Sem dados no período</div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={chart}>
              <defs>
                <linearGradient id="acumulado" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="data" stroke="#64748b" tick={{ fontSize: 11 }} />
              <YAxis stroke="#64748b" tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }} formatter={(v) => fmtBRL(v)} />
              <Area type="monotone" dataKey="Acumulado" stroke="#10b981" strokeWidth={2} fill="url(#acumulado)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {chart.length > 0 && (
        <div className="bg-slate-900 border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4">Movimentação Diária</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="data" stroke="#64748b" tick={{ fontSize: 11 }} />
              <YAxis stroke="#64748b" tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }} formatter={(v) => fmtBRL(v)} />
              <Legend />
              <Line type="monotone" dataKey="Entradas" stroke="#10b981" strokeWidth={2} />
              <Line type="monotone" dataKey="Saídas" stroke="#ef4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
